import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import CountUp from "@/components/CountUp";
import { Activity, Building2, Database, Sparkles } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function LiveCounter() {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/public/live-stats");
      setStats((prev) => {
        if (prev && (data.today_total !== prev.today_total || data.total_all !== prev.total_all)) {
          setPulse((p) => p + 1);
        }
        return data;
      });
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!stats) return null;
  const todayTotal = stats.today_total || 0;
  const totalAll = stats.total_all || 0;

  return (
    <section className="border-t border-border bg-gradient-to-b from-background to-muted/30" data-testid="live-counter-section">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-1 text-xs">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live feed
            </div>
            <h2 className="mt-3 font-heading font-bold text-2xl sm:text-3xl tracking-tight">
              Fresh companies delivered right now
            </h2>
          </div>
          <div className="text-xs text-muted-foreground num">
            Last upload {timeAgo(stats.latest_upload_at)} · refreshes every 30s
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testid="live-today-total"
            icon={Sparkles}
            tone="emerald"
            label="Added today"
            pulse={pulse}
            value={<CountUp value={todayTotal} />}
            hint={`${stats.today_gst.toLocaleString()} GST · ${stats.today_mca.toLocaleString()} MCA`}
          />
          <StatCard
            testid="live-total-all"
            icon={Database}
            tone="indigo"
            label="Total records"
            value={<CountUp value={totalAll} />}
            hint={`${stats.total_gst.toLocaleString()} GST · ${stats.total_mca.toLocaleString()} MCA`}
          />
          <StatCard
            testid="live-total-gst"
            icon={Building2}
            tone="indigo"
            label="GST companies"
            value={<CountUp value={stats.total_gst} />}
            hint="All-time"
          />
          <StatCard
            testid="live-total-mca"
            icon={Activity}
            tone="emerald"
            label="MCA companies"
            value={<CountUp value={stats.total_mca} />}
            hint="All-time"
          />
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone = "indigo", testid, pulse = 0 }) {
  const toneClasses = tone === "emerald"
    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
    : "bg-primary/10 text-primary";
  return (
    <div
      className="rounded-xl border border-border bg-card p-5 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-sm transition-all"
      data-testid={testid}
      key={`${testid}-${pulse}`}
    >
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-md grid place-items-center ${toneClasses}`}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-heading font-black text-3xl sm:text-4xl num tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground num">{hint}</div>
    </div>
  );
}
